import { Mentions } from "@/components/AssociationWith";
import { PostType } from "@/constants";
import { StaticImageData } from "next/image";
import { ReactNode } from "react";

export interface NavItem {
  name: string;
  to: string;
  icon?: React.ElementType;
  /** When set, used as the link href on large screens (lg+); `to` is used on smaller screens */
  toLg?: string;
}

export interface TrendingTopic1 {
  category: string;
  title: string;
  posts: string;
  image: string;
}

export interface SuggestedUser extends UserProfile {
  id: string;
  name: string;
  username: string;
  user_id: string;
  image: StaticImageData | string;
}

export interface PostMedia {
  type: string;
  url: string;
  thumbnail_url: string;
  processing_state: string;
}

export interface Media {
  id: string;
  type: string;
  url: string;
  thumbnail_url?: string;
  processing_state: string;
}

export interface Author {
  id: string | number;
  name: string;
  display_name: string | undefined;
  first_name?: string;
  last_name?: string;
  profile_bio?: string;
  profile_picture?: {
    url: string;
    thumbnail_url: string;
  };
}

export type Tag = {
  tag_id: string;
  tag_name: string;
};

export interface Mention {
  id: string;
  name: string;
  profile_picture?: {
    url: string;
  };
  profile_bio: string;
}

export interface Post {
  id: string;
  author: Author;
  profile_bio?: string;
  username: string;
  handle: string;
  avatar: string;
  content: string;
  type: PostType;
  timestamp: string;
  media?: Media[];
  likes?: number;
  total_reactions: number;
  impressions: number;
  total_comments: number;
  comments: number;
  reaction_by_me: ReactionByMe | undefined;
  reposts: number;
  location?: {
    type: string;
    coordinates: number[];
  };
  location_name?: string;
  links?: string[];
  seconds_ago: number;
  ai_analysis: AiAnalysis | null;
  createdAt: string;
  mentioned?: Mention[];
  associated_with?: Mentions[];
  tags?: Tag[];
  isUserPost?: boolean;
  automated_post?: boolean;
}

export interface TrendingTopic {
  id: number;
  topic: string;
  posts: string;
}

export interface Follower {
  id: string;
  name: string;
  username: string;
  bio?: string;
  isFollowing: boolean;
  profile_picture?: {
    url?: string;
    type?: string | null;
  } | null;
  avatar: StaticImageData | string;
}

export interface FollowingUser {
  id: string;
  name: string;
  username: string;
  bio: string;
  isFollowing: boolean;
  profile_picture?: {
    url?: string;
    type?: string | null;
  } | null;
  avatar: StaticImageData | string;
}

export interface Patronage {
  id: string;
  name: string;
  username: string;
  bio: string;
  tier: "Platinum" | "Gold" | "Silver" | string;
  patronSince: string;
  profile_picture?: {
    url?: string;
    type?: string | null;
  } | null;
  avatar: StaticImageData | string;
}

export interface Patron {
  id: string;
  name: string;
  username: string;
  bio: string;
  patronSince: string;
  profile_picture?: {
    url?: string;
    type?: string | null;
  } | null;
  tier: string;
  avatar: string | StaticImageData;
}

export interface UserProfile {
  username: string;
  userId: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  account_type?: string;
  verification_status?: string;
  is_political_party?: boolean;
  party_election_symbol?: {
    id: string;
    url: string;
    thumbnail_url: string;
  };
  partyLogo?: string;
  privacy_level?: string;
  problem_praise_tab?: string;
  email_address?: string;
  profile_bio: string;
  createdAt: string;
  profileImage: string;
  profileImageMediaId?: string;
  coverPhoto?: string;
  profile_banner?: {
    id?: string;
    url: string;
    thumbnail_url?: string;
  };
  following: number;
  followers: number;
  patrons: number;
  patronage: number;
  profile_picture?: {
    media_id: string;
    url: string;
  };
  location?: {
    type: string;
    coordinates: number[];
    formattedAddress?: string;
  };
  joinDate?: string;
  total_posts: number;
  problem_posts: number;
  praise_posts: number;
  marketplace_posts?: number;
}

// This is the type for the `mentioned` users *after* mapping
export interface FormattedMentionedUser {
  id: string;
  name: string;
  avatar: string;
  profile_bio?: string; // URL string
}

// This is the type for the `tags` *after* mapping
export interface FormattedTag {
  id: string; // Maps from tag_id
  name: string; // Maps from tag_name
}

export interface AiAnalysis {
  suggestions: string[];
  responsible_entities: string[];
}
// --- The main type you requested ---
export interface FormattedPost {
  id: string;
  profile_bio?: string;
  display_name: string | undefined;
  username: string;
  handle: string;
  type: PostType; // Using the PostType enum
  avatar: string; // URL string
  content: string;
  media?: Media[]; // Retaining array of any, or define specific media types
  total_reactions: number;
  impressions: number;
  reaction_by_me: ReactionByMe | undefined;
  total_comments: number;
  comments: number;
  reposts: number; // Guaranteed to be a number (0 if original was undefined)
  location: string | null; // Nullable string
  location_name: string | null; // Nullable string
  location_coordinates?: number[]; // [lng, lat] coordinates for location search
  links?: any[]; // Retaining array of any, or define specific link types
  timestamp: string; // Output of formatTimestamp()
  mentioned?: FormattedMentionedUser[]; // Array of formatted mentioned users
  tags?: FormattedTag[]; // Array of formatted tags
  isUserPost: boolean; // Output of isUserPost()
  author: Author; // The original author object
  seconds_ago: number;
  createdAt: string; // The original createdAt string
  likes?: number;
  associated_with?: any[];
  ai_analysis: AiAnalysis | null;
  automated_post?: boolean;
}

export enum ReactionType {
  LIKE = "LIKE",
  SMILE = "SMILE",
}

export enum FeedType {
  GENERAL = "GENERAL",
  FOLLOWING = "FOLLOWING",
  PATRONAGE = "PATRONAGE",
  USER_POSTS = "USER_POSTS",
  CLIPS = "CLIPS",
}
export interface ReactionByMe {
  reaction_type: ReactionType;
}

export interface CommentUser {
  id: string;
  name: string;
  profile_picture?: {
    url: string;
  };
}

export interface TagInfo {
  id: string;
  name: string;
}

export interface Comment {
  id: string;
  content: string;
  text: string; // For backward compatibility
  mentioned: CommentUser[];
  tags: TagInfo[];
  name: string;
  username: string;
  profile_picture?: {
    url: string;
  };
  profileImage: string | null; // For backward compatibility
  likes: number;
  liked: boolean;
  retweets: number;
  timestamp: number;
  isMine: boolean;
  replies: any[];
}

export interface Reaction {
  reaction_id: string;
  reaction_by: {
    id: string;
    name: string;
    profile_picture?: {
      url: string;
    };
  };
  reaction_type: ReactionType;
}

export enum PopularityCriteria {
  FOLLOWERS = "FOLLOWERS",
  PATRONS = "PATRONS",
}

export interface PopularUserDetails {
  id: string;
  name: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  profile_bio?: string;
  profile_picture?: {
    url: string;
  };
  follower_count: number;
  patron_count: number;
  total_association_score: number;
}

export interface PopularUserInput {
  first: number;
  criteria: PopularityCriteria;
  after_name?: string;
  after_association_score?: string;
}

// Interface for the GraphQL response structure
interface ExplorePostResponse {
  id: string;
  type: string;
  content: string;
  author: {
    id: string;
    name: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    profile_bio?: string;
    profile_picture?: {
      id: string;
      type: string;
      url: string;
    };
  };
  likes?: number;
  total_reactions: number;
  mentioned?: Array<{
    id: string;
    name: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    profile_bio?: string;
    profile_picture?: {
      id: string;
      type: string;
      url: string;
    };
  }>;
  tags?: Array<{
    tag_id: string;
    tag_name: string;
  }>;
  total_comments: number;
  reaction_by_me?: {
    reaction_type: string;
  };
  comments: number;
  seconds_ago: number;
  media?: Array<{
    id: string;
    type: string;
    url: string;
  }>;
  links?: string[];
  location?: {
    type: string;
    coordinates: number[];
  };
  location_name?: string;
  createdAt: string;
  associated_with?: Array<{
    id: string;
    name: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
    profile_bio?: string;
    profile_picture?: {
      id: string;
      type: string;
      url: string;
    };
  }>;
  automated_post?: boolean;
}
